/**
 * Figure glyphs — headless port of `arch.ui/glyphs.js` device/actor
 * illustrations for the DOM-free scene kernel.
 *
 * The live widgets theme off a single CSS custom property (`--fig-accent`)
 * and derive every shade with `color-mix(in oklab, …)`. The SVG export
 * backend (resvg) resolves neither CSS custom properties nor `color-mix()`,
 * so here every colour is pre-resolved to a concrete value: we compute a
 * small `FigurePalette` from the figure's accent (using the same OKLab
 * `mix()` the tokens module uses, so headless colours agree with the board)
 * and stamp concrete hex / rgba into the markup.
 *
 * Gradient / clip ids are namespaced per figure instance via an `idPrefix`
 * param: a single figure stamps several internal gradients, and many figures
 * can share one board SVG, so every `id="…"` and matching `url(#…)` is
 * prefixed to avoid cross-figure collisions.
 */

import { LINE_HEIGHT } from "./metrics.js";
import { mix, alpha, type Tokens, defaultTokens } from "./tokens.js";
import { box, glyph, text, type BoxNode, type FontSpec, type Node } from "./model.js";

// ───────────────────────────────────────────────────────────────────────────
// Palette
// ───────────────────────────────────────────────────────────────────────────

/** Concrete shades derived from a single accent hue — the headless
 *  resolution of the live widgets' `--fig-accent` + `color-mix` chain. */
export interface FigurePalette {
  /** `var(--fig-accent)` */
  readonly accent: string;
  /** `color-mix(in oklab, accent 62%, white)` */
  readonly accentLight: string;
  /** `color-mix(in oklab, accent 72%, black)` */
  readonly accentDark: string;
  /** `color-mix(in oklab, accent 50%, black)` */
  readonly accentDeep: string;
  /** `var(--fig-ink)` */
  readonly ink: string;
  /** `var(--fig-paper)` */
  readonly paper: string;
  /** `color-mix(in oklab, paper 70%, transparent)` */
  readonly paperDim: string;
}

export function makeFigurePalette(accent: string): FigurePalette {
  return {
    accent,
    // color-mix(accent X%, white/black) blends toward white/black by (1 - X);
    // OKLab mix(a, b, t) goes a→b by t, so t = the "rest" fraction.
    accentLight: mix(accent, "#ffffff", 0.38),
    accentDark: mix(accent, "#000000", 0.28),
    accentDeep: mix(accent, "#000000", 0.5),
    ink: "#0b1220",
    paper: "#f1f5f9",
    paperDim: alpha("#f1f5f9", 0.7),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Shared markup helpers (colours pre-resolved)
// ───────────────────────────────────────────────────────────────────────────

/** `color-mix(in oklab, paper 70%, transparent)` — resolved once. */
const PAPER_DIM = alpha("#f1f5f9", 0.7);

/** A linear gradient def, top→bottom by default. `id` must already be
 *  namespaced by the caller (`${idPrefix}…`). */
function linGrad(
  id: string,
  from: string,
  to: string,
  opts: { x1?: number; y1?: number; x2?: number; y2?: number } = {},
): string {
  const { x1 = 0, y1 = 0, x2 = 0, y2 = 1 } = opts;
  return `<linearGradient id="${id}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}">
    <stop offset="0" stop-color="${from}"/>
    <stop offset="1" stop-color="${to}"/>
  </linearGradient>`;
}

/** A soft top-left specular highlight gradient (white → transparent). */
function glossGrad(id: string): string {
  return `<linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0" stop-color="rgba(255,255,255,0.55)"/>
    <stop offset="0.5" stop-color="rgba(255,255,255,0.06)"/>
    <stop offset="1" stop-color="rgba(255,255,255,0)"/>
  </linearGradient>`;
}

/** A short row of "content" lines for screens — pure decoration. */
function screenLines(
  x: number,
  y: number,
  w: number,
  gap: number,
  n: number,
  opts: { color?: string; h?: number; last?: number } = {},
): string {
  const { color = PAPER_DIM, h = 2.4, last = 0.6 } = opts;
  let out = "";
  for (let i = 0; i < n; i++) {
    const lw = i === n - 1 ? w * last : w * (0.7 + (0.3 * ((i * 37) % 10)) / 10);
    out += `<rect x="${x}" y="${y + i * gap}" width="${lw.toFixed(1)}" height="${h}" rx="${h / 2}" fill="${color}"/>`;
  }
  return out;
}

/** A small grid of rounded app squares for the plain smartphone face. */
function appGridMarkup(
  pal: FigurePalette,
  x: number,
  y: number,
  size: number,
  gap: number,
  cols: number,
): string {
  const cell = (size - gap * (cols - 1)) / cols;
  let out = "";
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < cols; c++) {
      const cx = x + c * (cell + gap);
      const cy = y + r * (cell + gap);
      const tone = (r + c) % 2 === 0 ? pal.accent : pal.accentLight;
      out += `<rect x="${cx.toFixed(1)}" y="${cy.toFixed(1)}" width="${cell.toFixed(1)}" height="${cell.toFixed(1)}" rx="2.5" fill="${tone}" opacity="0.9"/>`;
    }
  }
  return out;
}

/** The shape each figure glyph returns — inner-SVG `body` against a
 *  `0 0 viewW viewH` viewBox. */
export interface GlyphResult {
  readonly viewW: number;
  readonly viewH: number;
  readonly body: string;
}

// ───────────────────────────────────────────────────────────────────────────
// People
// ───────────────────────────────────────────────────────────────────────────

interface PersonOpts {
  id?: string;
  scale?: number;
  dx?: number;
  dy?: number;
  dim?: number;
  flat?: boolean;
}

/**
 * A single person — round head, soft blob body. viewBox 0 0 100 120.
 * `scale`/`dx`/`dy` let callers stamp several into one drawing
 * (user-group). `dim` fades back-rank figures for depth. Returns the raw
 * `<g>` fragment (userGroupGlyph stamps three).
 */
function personGlyph(pal: FigurePalette, idPrefix: string, opts: PersonOpts = {}): string {
  const { id = "p", scale = 1, dx = 0, dy = 0, dim = 1, flat = false } = opts;
  const gid = `${idPrefix}${id}`;
  const bodyFill = flat ? pal.accent : `url(#${gid}-body)`;
  const headFill = flat ? pal.accentLight : `url(#${gid}-head)`;
  const defs = flat
    ? ""
    : `<defs>
      ${linGrad(`${gid}-body`, pal.accentLight, pal.accentDark)}
      ${linGrad(`${gid}-head`, pal.accentLight, pal.accent)}
      ${glossGrad(`${gid}-gloss`)}
    </defs>`;
  return `<g transform="translate(${dx} ${dy}) scale(${scale})" opacity="${dim}">
    ${defs}
    <path d="M14 120 C14 84 31 70 50 70 C69 70 86 84 86 120 Z"
          fill="${bodyFill}" stroke="${pal.accentDeep}" stroke-width="1.5"/>
    ${flat ? "" : `<path d="M20 118 C20 88 33 76 50 76 C58 76 64 78 69 82 C50 84 34 96 30 120 Z"
          fill="url(#${gid}-gloss)" opacity="0.7"/>`}
    <circle cx="50" cy="40" r="26" fill="${headFill}" stroke="${pal.accentDeep}" stroke-width="1.5"/>
    ${flat ? "" : `<ellipse cx="42" cy="31" rx="11" ry="8" fill="rgba(255,255,255,0.45)"/>`}
  </g>`;
}

/** A single user. viewBox 0 0 100 120. */
export function userGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return { viewW: 100, viewH: 120, body: personGlyph(pal, idPrefix, { id: "u" }) };
}

/** Three people, front-and-centre with two behind. viewBox 0 0 140 120. */
export function userGroupGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return {
    viewW: 140,
    viewH: 120,
    body: `
      ${personGlyph(pal, idPrefix, { id: "ugL", scale: 0.74, dx: -2, dy: 30, dim: 0.85 })}
      ${personGlyph(pal, idPrefix, { id: "ugR", scale: 0.74, dx: 68, dy: 30, dim: 0.85 })}
      ${personGlyph(pal, idPrefix, { id: "ugC", scale: 0.92, dx: 30, dy: 8 })}
    `,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Surfaces / devices
// ───────────────────────────────────────────────────────────────────────────

/** Browser window — chrome bar, traffic dots, address pill, page body. */
export function browserGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return {
    viewW: 140,
    viewH: 104,
    body: `
      <defs>
        ${linGrad(`${idPrefix}br-frame`, mix("#1e293b", pal.accent, 0.3), "#0f172a")}
        ${linGrad(`${idPrefix}br-page`, pal.paper, mix("#f1f5f9", pal.accent, 0.14))}
      </defs>
      <rect x="3" y="3" width="134" height="98" rx="9" fill="url(#${idPrefix}br-frame)"
            stroke="${pal.accent}" stroke-width="2"/>
      <rect x="9" y="22" width="122" height="73" rx="4" fill="url(#${idPrefix}br-page)"/>
      <circle cx="14" cy="12.5" r="3" fill="#f87171"/>
      <circle cx="24" cy="12.5" r="3" fill="#fbbf24"/>
      <circle cx="34" cy="12.5" r="3" fill="#34d399"/>
      <rect x="46" y="8.5" width="85" height="8" rx="4" fill="rgba(255,255,255,0.16)"/>
      <rect x="16" y="30" width="46" height="30" rx="4" fill="${pal.accent}" opacity="0.85"/>
      ${screenLines(70, 32, 52, 8, 4, { color: alpha("#0b1220", 0.35) })}
      ${screenLines(16, 70, 108, 8, 3, { color: alpha("#0b1220", 0.22), last: 0.5 })}
    `,
  };
}

/** Laptop — lid with a mini page, hinged deck. viewBox 0 0 150 108. */
export function laptopGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return {
    viewW: 150,
    viewH: 108,
    body: `
      <defs>
        ${linGrad(`${idPrefix}lt-lid`, "#1e293b", "#0f172a")}
        ${linGrad(`${idPrefix}lt-screen`, mix("#0f172a", pal.accent, 0.22), "#0f172a")}
        ${linGrad(`${idPrefix}lt-deck`, "#cbd5e1", "#94a3b8")}
      </defs>
      <rect x="22" y="6" width="106" height="74" rx="7" fill="url(#${idPrefix}lt-lid)"
            stroke="${pal.accent}" stroke-width="2"/>
      <rect x="29" y="13" width="92" height="60" rx="3" fill="url(#${idPrefix}lt-screen)"/>
      <rect x="35" y="19" width="38" height="22" rx="3" fill="${pal.accent}" opacity="0.9"/>
      ${screenLines(78, 20, 38, 6.5, 3, { color: pal.paperDim })}
      ${screenLines(35, 47, 80, 6.5, 3, { color: pal.paperDim, last: 0.55 })}
      <path d="M10 80 H140 L150 96 a4 4 0 0 1 -4 4 H4 a4 4 0 0 1 -4 -4 Z"
            fill="url(#${idPrefix}lt-deck)"/>
      <rect x="58" y="84" width="34" height="5" rx="2.5" fill="#64748b"/>
    `,
  };
}

/** Tablet — portrait slab, camera, home indicator. viewBox 0 0 108 140. */
export function tabletGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return {
    viewW: 108,
    viewH: 140,
    body: `
      <defs>
        ${linGrad(`${idPrefix}tb-body`, "#1e293b", "#0b1220")}
        ${linGrad(`${idPrefix}tb-screen`, mix("#0f172a", pal.accent, 0.2), "#0f172a")}
      </defs>
      <rect x="6" y="4" width="96" height="132" rx="12" fill="url(#${idPrefix}tb-body)"
            stroke="${pal.accent}" stroke-width="2"/>
      <rect x="14" y="16" width="80" height="108" rx="4" fill="url(#${idPrefix}tb-screen)"/>
      <circle cx="54" cy="10" r="1.6" fill="#475569"/>
      <rect x="44" y="129" width="20" height="3" rx="1.5" fill="#475569"/>
      <rect x="22" y="24" width="64" height="30" rx="4" fill="${pal.accent}" opacity="0.85"/>
      ${screenLines(22, 62, 64, 8, 4, { color: pal.paperDim, last: 0.5 })}
    `,
  };
}

/**
 * Smartphone — portrait, notch, home bar. `logo` is optional inner-SVG
 * markup centred on the screen (apple / android badges plug in here);
 * with no logo the screen shows a small app grid. viewBox 0 0 72 140.
 */
export function phoneGlyph(pal: FigurePalette, idPrefix: string, logo = ""): GlyphResult {
  const appGrid = logo ? "" : appGridMarkup(pal, 20, 40, 32, 9, 3);
  const logoLayer = logo ? `<g transform="translate(36 70)">${logo}</g>` : "";
  return {
    viewW: 72,
    viewH: 140,
    body: `
      <defs>
        ${linGrad(`${idPrefix}ph-body`, "#1e293b", "#0b1220")}
        ${linGrad(`${idPrefix}ph-screen`, mix("#0f172a", pal.accent, 0.26), "#0f172a")}
      </defs>
      <rect x="8" y="3" width="56" height="134" rx="14" fill="url(#${idPrefix}ph-body)"
            stroke="${pal.accent}" stroke-width="2"/>
      <rect x="13" y="13" width="46" height="114" rx="7" fill="url(#${idPrefix}ph-screen)"/>
      <rect x="28" y="8.5" width="16" height="4" rx="2" fill="#0b1220"/>
      <rect x="29" y="130" width="14" height="2.6" rx="1.3" fill="#64748b"/>
      ${appGrid}
      ${logoLayer}
    `,
  };
}

/** Apple-style mark — rounded twin-lobe apple with a leaf. Centred at
 *  (0,0), ~26 tall. */
export function appleLogo(pal: FigurePalette): string {
  return `<g transform="translate(-12 -13)">
    <path d="M12 6 C9 2 3 3 2 9 C1 14 4 24 9 24 C10.5 24 11 23 12 23 C13 23 13.5 24 15 24 C20 24 23 14 22 9 C21 3 15 2 12 6 Z"
          fill="${pal.paper}"/>
    <path d="M12 6 C12 2 14 0 17.5 0 C17.5 3.5 15.5 6.5 12 6.5 Z"
          fill="${pal.paper}"/>
  </g>`;
}

/** Android-style robot mark. Centred at (0,0), ~26 tall. */
export function androidLogo(pal: FigurePalette): string {
  return `<g transform="translate(-15 -13)">
    <path d="M6 13 a9 9 0 0 1 18 0 Z" fill="${pal.paper}"/>
    <rect x="6" y="13" width="18" height="14" rx="3" fill="${pal.paper}"/>
    <rect x="2.5" y="14" width="3.5" height="11" rx="1.75" fill="${pal.paper}"/>
    <rect x="24" y="14" width="3.5" height="11" rx="1.75" fill="${pal.paper}"/>
    <rect x="10" y="26" width="3.5" height="9" rx="1.75" fill="${pal.paper}"/>
    <rect x="16.5" y="26" width="3.5" height="9" rx="1.75" fill="${pal.paper}"/>
    <circle cx="11" cy="9.5" r="1.4" fill="${pal.ink}"/>
    <circle cx="19" cy="9.5" r="1.4" fill="${pal.ink}"/>
    <line x1="8" y1="3" x2="11" y2="7" stroke="${pal.paper}" stroke-width="1.4" stroke-linecap="round"/>
    <line x1="22" y1="3" x2="19" y2="7" stroke="${pal.paper}" stroke-width="1.4" stroke-linecap="round"/>
  </g>`;
}

/** Desktop computer — monitor on a stand with a keyboard. viewBox 0 0 140 124. */
export function desktopGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return {
    viewW: 140,
    viewH: 124,
    body: `
      <defs>
        ${linGrad(`${idPrefix}dt-bezel`, "#1e293b", "#0b1220")}
        ${linGrad(`${idPrefix}dt-screen`, mix("#0f172a", pal.accent, 0.24), "#0f172a")}
        ${linGrad(`${idPrefix}dt-stand`, "#94a3b8", "#64748b")}
      </defs>
      <rect x="6" y="4" width="128" height="82" rx="8" fill="url(#${idPrefix}dt-bezel)"
            stroke="${pal.accent}" stroke-width="2"/>
      <rect x="13" y="11" width="114" height="68" rx="3" fill="url(#${idPrefix}dt-screen)"/>
      <rect x="19" y="17" width="44" height="26" rx="3" fill="${pal.accent}" opacity="0.9"/>
      ${screenLines(68, 18, 52, 7, 3, { color: pal.paperDim })}
      ${screenLines(19, 49, 100, 7, 3, { color: pal.paperDim, last: 0.55 })}
      <path d="M58 86 h24 l4 14 h-32 Z" fill="url(#${idPrefix}dt-stand)"/>
      <rect x="44" y="100" width="52" height="6" rx="3" fill="#475569"/>
      <rect x="16" y="112" width="108" height="9" rx="3" fill="url(#${idPrefix}dt-stand)"/>
    `,
  };
}

/** Server / rack — stacked units with status LEDs and vents. viewBox 0 0 96 140. */
export function serverGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  const unit = (y: number, on: boolean) => `
    <rect x="8" y="${y}" width="80" height="34" rx="5"
          fill="url(#${idPrefix}sv-unit)" stroke="${pal.accent}" stroke-width="1.5"/>
    <circle cx="18" cy="${y + 11}" r="3" fill="${on ? "#34d399" : "#475569"}"/>
    <circle cx="28" cy="${y + 11}" r="3" fill="${on ? pal.accent : "#475569"}"/>
    <rect x="40" y="${y + 8}" width="40" height="4" rx="2" fill="rgba(255,255,255,0.18)"/>
    <rect x="40" y="${y + 18}" width="40" height="4" rx="2" fill="rgba(255,255,255,0.1)"/>
    <rect x="18" y="${y + 22}" width="14" height="5" rx="2.5" fill="rgba(148,163,184,0.5)"/>`;
  return {
    viewW: 96,
    viewH: 140,
    body: `
      <defs>
        ${linGrad(`${idPrefix}sv-unit`, "#243049", "#10182b")}
      </defs>
      <rect x="4" y="4" width="88" height="132" rx="9" fill="#0b1220"
            stroke="${pal.accentDeep}" stroke-width="2"/>
      ${unit(12, true)}
      ${unit(53, true)}
      ${unit(94, false)}
    `,
  };
}

/** Cloud — puffy gradient cumulus with a soft top highlight. viewBox 0 0 150 96. */
export function cloudGlyph(pal: FigurePalette, idPrefix: string): GlyphResult {
  return {
    viewW: 150,
    viewH: 96,
    body: `
      <defs>
        ${linGrad(`${idPrefix}cl-fill`, pal.accentLight, pal.accentDark)}
        ${glossGrad(`${idPrefix}cl-gloss`)}
      </defs>
      <path d="M40 84
               a26 26 0 0 1 -3 -51
               a30 30 0 0 1 56 -8
               a22 22 0 0 1 18 21
               a19 19 0 0 1 -5 38 Z"
            fill="url(#${idPrefix}cl-fill)" stroke="${pal.accentDeep}" stroke-width="2"/>
      <path d="M40 84
               a26 26 0 0 1 -3 -51
               a30 30 0 0 1 56 -8
               a22 22 0 0 1 18 21
               a19 19 0 0 1 -5 38 Z"
            fill="url(#${idPrefix}cl-gloss)" opacity="0.55"/>
    `,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Registry — mirrors arch.extras/widgets.js `defineFigure` (tag → accent +
// intrinsic size + glyph).
// ───────────────────────────────────────────────────────────────────────────

export const FIGURE_SPECS: Record<
  string,
  { accent: string; w: number; h: number; glyph: (pal: FigurePalette, idPrefix: string) => GlyphResult }
> = {
  "arch-user": { accent: "#fbbf24", w: 116, h: 150, glyph: userGlyph },
  "arch-usergroup": { accent: "#f59e0b", w: 156, h: 140, glyph: userGroupGlyph },
  "arch-webpage": { accent: "#38bdf8", w: 188, h: 142, glyph: browserGlyph },
  "arch-laptop": { accent: "#818cf8", w: 184, h: 134, glyph: laptopGlyph },
  "arch-tablet": { accent: "#a78bfa", w: 120, h: 156, glyph: tabletGlyph },
  "arch-smartphone": { accent: "#34d399", w: 92, h: 168, glyph: (pal, id) => phoneGlyph(pal, id) },
  "arch-iphone": { accent: "#94a3b8", w: 92, h: 168, glyph: (pal, id) => phoneGlyph(pal, id, appleLogo(pal)) },
  "arch-android": { accent: "#3ddc84", w: 92, h: 168, glyph: (pal, id) => phoneGlyph(pal, id, androidLogo(pal)) },
  "arch-desktop": { accent: "#60a5fa", w: 168, h: 150, glyph: desktopGlyph },
  "arch-server": { accent: "#22d3ee", w: 108, h: 158, glyph: serverGlyph },
  "arch-cloud": { accent: "#7dd3fc", w: 180, h: 120, glyph: cloudGlyph },
};

// ───────────────────────────────────────────────────────────────────────────
// Composition
// ───────────────────────────────────────────────────────────────────────────

/**
 * Height of the name caption under a figure glyph: one text line (the scene
 * metrics' shared line height) plus the column gap. The solver adds this to
 * the drawing's own height (see `shapeSizeRegistry`) so the glyph renders at
 * its DESIGNED proportions with the name under it — sizing the node to the
 * drawing alone leaves the caption zero room, and the HTML backend then
 * floors the svg at its aspect-transferred height and crushes the name.
 */
export function figureCaptionBand(t: Tokens = defaultTokens()): number {
  return Math.round(t.fontSize * LINE_HEIGHT) + t.space1;
}

/**
 * Compose a figure card — the illustration glyph filling the box with a
 * centred name caption below, like `composePersonFigure` in shapes.ts.
 * Returns `null` for an unknown tag.
 *
 * A fixed local `idPrefix` ("fig-") is enough: the board render wraps each
 * node in its own `n{seq}_` prefix, so a constant suffix stays unique across
 * figures while keeping this figure's internal gradients namespaced.
 */
export function composeFigureCard(
  tag: string,
  name: string,
  t: Tokens = defaultTokens(),
  dims?: { width: number; height: number },
  accentOverride?: string,
  caption?: string,
): BoxNode | null {
  const spec = FIGURE_SPECS[tag];
  if (!spec) return null;
  const d = dims ?? { width: spec.w, height: spec.h };
  const accent = accentOverride ?? spec.accent;
  const pal = makeFigurePalette(accent);
  // Namespace the glyph's internal gradient/clip ids by tag + accent so two
  // same-type figures with different accents on one board SVG don't collide
  // (same accent → same id is harmless: identical gradient). The glyph body is
  // baked raw, so toSVG's per-node prefix can't reach these ids.
  const idPrefix = `f${tag.replace(/[^a-z0-9]/gi, "")}${accent.replace(/[^a-z0-9]/gi, "")}-`;
  const { viewW, viewH, body } = spec.glyph(pal, idPrefix);
  // Uniform type scale: the caption never follows the figure's size — a bigger
  // device figure is a bigger GLYPH with the same-size label under it.
  const capFont: FontSpec = { family: t.fontFamily, size: t.fontSize, weight: 600 };
  // The glyph takes the box MINUS the caption band, so the name always has
  // its line. With the solver's figure size (drawing + band) that puts the
  // glyph at exactly its designed height; under an explicit smaller
  // `widget.height` the glyph gives way first (grow absorbs the overflow).
  const glyphH = Math.max(24, d.height - figureCaptionBand(t));
  const children: Node[] = [
    glyph({ viewW, viewH, width: d.width, height: glyphH, grow: 1, body }),
    text(name, { font: capFont, color: t.fgSoft, align: "center", ellipsis: true }),
  ];
  // Optional muted sub-line (widget.caption / subheader) — the arch.extras
  // figure's second line. Band-gated: reveals as the node zooms in, like the
  // card's body detail, rather than always painting over the small glyph.
  if (caption) {
    const subFont: FontSpec = { ...capFont, size: capFont.size * 0.82, weight: 500 };
    // Wrap in a reveal box: the opacity band var is emitted on boxes, not text.
    children.push(box({
      direction: "row", justify: "center", reveal: "compact",
      children: [text(caption.toUpperCase(), { font: subFont, color: t.fgMuted, align: "center", ellipsis: true })],
    }));
  }
  return box({
    direction: "column",
    align: "center",
    gap: t.space1,
    children,
  });
}
